import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import DictationInput from '../DictationInput';
import VoiceRecorder from '../VoiceRecorder';
import { toast } from 'sonner';
import { FileText, Plus, Sparkles, Loader2 } from 'lucide-react';

export default function ResumeTab({ api, resume, setResume }) {
  const [resumeAI, setResumeAI] = useState({ employer: '', position: '', duration: '', tasks: '', skills: '' });
  const [resumeAIResult, setResumeAIResult] = useState(null);
  const [resumeAILoading, setResumeAILoading] = useState(false);

  const saveResume = async () => {
    try {
      await api.post('/resume/save', resume);
      toast.success('Resume saved!');
    } catch {
      toast.error('Failed to save resume');
    }
  };

  const addWorkHistory = () => {
    setResume(prev => ({ ...prev, work_history: [...prev.work_history, { company: '', title: '', dates: '', description: '' }] }));
  };

  const updateWorkHistory = (index, field, value) => {
    setResume(prev => ({ ...prev, work_history: prev.work_history.map((w, i) => i === index ? { ...w, [field]: value } : w) }));
  };

  const removeWorkHistory = (index) => {
    setResume(prev => ({ ...prev, work_history: prev.work_history.filter((_, i) => i !== index) }));
  };

  const handleAIFormat = async () => {
    setResumeAILoading(true);
    try {
      const r = await api.post('/ai/resume-format', { answers: resumeAI });
      setResumeAIResult(r.data);
      toast.success('Resume formatted!');
    } catch {
      toast.error('AI formatting failed');
    } finally {
      setResumeAILoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* AI Resume Builder */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-violet-50 to-sky-50" data-testid="ai-resume-top">
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" />AI Resume Builder</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Answer a few questions and AI will format your experience professionally.</p>
          <div className="space-y-2">
            <div className="flex gap-1"><Input placeholder="Last employer?" value={resumeAI.employer} onChange={e => setResumeAI({...resumeAI, employer: e.target.value})} data-testid="ai-resume-employer" className="flex-1" /><VoiceRecorder size="icon" onTranscription={t => setResumeAI(p => ({...p, employer: t}))} /></div>
            <div className="flex gap-1"><Input placeholder="Position held?" value={resumeAI.position} onChange={e => setResumeAI({...resumeAI, position: e.target.value})} className="flex-1" /><VoiceRecorder size="icon" onTranscription={t => setResumeAI(p => ({...p, position: t}))} /></div>
            <div className="flex gap-1"><Input placeholder="How long? (e.g. 2 years)" value={resumeAI.duration} onChange={e => setResumeAI({...resumeAI, duration: e.target.value})} className="flex-1" /><VoiceRecorder size="icon" onTranscription={t => setResumeAI(p => ({...p, duration: t}))} /></div>
            <DictationInput value={resumeAI.tasks} onChange={(cleaned) => setResumeAI(p => ({...p, tasks: cleaned}))} rows={2} placeholder="What tasks did you perform?" data-testid="ai-resume-tasks" />
            <div className="flex gap-1"><Input placeholder="Skills used? (comma separated)" value={resumeAI.skills} onChange={e => setResumeAI({...resumeAI, skills: e.target.value})} className="flex-1" /><VoiceRecorder size="icon" onTranscription={t => setResumeAI(p => ({...p, skills: p.skills ? `${p.skills}, ${t}` : t}))} /></div>
          </div>
          <Button disabled={resumeAILoading || !resumeAI.employer} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2" data-testid="ai-format-resume" onClick={handleAIFormat}>
            {resumeAILoading ? <><Loader2 size={14} className="animate-spin" /> Formatting...</> : <><Sparkles size={14} /> Format with AI</>}
          </Button>
          {resumeAIResult && (
            <div className="bg-white rounded-xl p-4 border space-y-2">
              <p className="font-semibold text-sm">{resumeAIResult.professional_title}</p>
              <p className="text-xs text-muted-foreground">{resumeAIResult.company} &middot; {resumeAIResult.duration}</p>
              <ul className="list-disc list-inside space-y-1">{resumeAIResult.bullet_points?.map((b, i) => <li key={`item-${i}`} className="text-sm">{b}</li>)}</ul>
              {resumeAIResult.skills_extracted?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{resumeAIResult.skills_extracted.map((s, i) => <Badge key={`item-${i}`} variant="secondary" className="text-xs">{s}</Badge>)}</div>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Personal Info */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-teal-600" /> Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Full Name</Label><Input value={resume.full_name} onChange={e => setResume({...resume, full_name: e.target.value})} placeholder="Your full name" data-testid="resume-name" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={resume.phone} onChange={e => setResume({...resume, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={resume.email} onChange={e => setResume({...resume, email: e.target.value})} placeholder="your.email@example.com" /></div>
          <div className="space-y-1.5">
            <Label>Professional Summary</Label>
            <DictationInput value={resume.summary} onChange={(cleaned) => setResume({...resume, summary: cleaned})} placeholder="Brief summary of your experience and goals..." rows={3} promptContext={{ context: 'employment', taskName: 'Professional Summary', taskDescription: 'Summarize experience and goals', category: 'employment' }} data-testid="resume-summary" />
          </div>
        </CardContent>
      </Card>

      {/* Work History */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">Work History</CardTitle>
          <Button variant="outline" size="sm" onClick={addWorkHistory} className="rounded-full" data-testid="add-work-history"><Plus size={14} className="mr-1" /> Add</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {resume.work_history.length > 0 ? (
            resume.work_history.map((work, index) => (
              <div key={`item-${index}`} className="p-4 rounded-xl bg-muted/30 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input value={work.company} onChange={e => updateWorkHistory(index, 'company', e.target.value)} placeholder="Company name" />
                  <Input value={work.title} onChange={e => updateWorkHistory(index, 'title', e.target.value)} placeholder="Job title" />
                </div>
                <Input value={work.dates} onChange={e => updateWorkHistory(index, 'dates', e.target.value)} placeholder="Dates (e.g., Jan 2023 - Present)" />
                <DictationInput value={work.description} onChange={(cleaned) => updateWorkHistory(index, 'description', cleaned)} placeholder="Key responsibilities and achievements..." rows={2} promptContext={{ context: 'employment', taskName: `${work.title || 'Work'} responsibilities`, taskDescription: 'Describe responsibilities and outcomes', category: 'employment' }} data-testid={`work-desc-${index}`} />
                <Button variant="ghost" size="sm" className="text-red-600 text-xs" onClick={() => removeWorkHistory(index)}>Remove</Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No work history added yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Skills */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Skills</CardTitle></CardHeader>
        <CardContent>
          <Input value={resume.skills.join(', ')} onChange={e => setResume({...resume, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="e.g., Customer Service, Forklift Certified, Time Management" />
          <p className="text-xs text-muted-foreground mt-2">Separate skills with commas</p>
        </CardContent>
      </Card>

      <Button onClick={saveResume} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-resume-button">Save Resume</Button>
    </div>
  );
}
